import { useRef } from "react";
import { message } from "antd";
import { v4 as uuidv4 } from "uuid";
import { getOAuthHeaders } from "../../../../constants/oAuthValidation";
import {
  getProductsNearbyStockService,
  upsertGiftCardTransaction,
  upsertCreditTransaction,
  upsertWalletTransactionService,
  discardCouponService,
  upsertCart,
  cartHistory,
} from "../../../../services";
import sortBy from "lodash/sortBy";
import db from "../../../../database";
import dayjs from "dayjs";
import openDrawer from "../../../../lib/printer/openDrawer";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import useCartStore from "../../../../stores/useCartStore";
import usePOSConfigStore from "../../../../stores/usePOSConfigStore";
import useSessionStore from "../../../../stores/useSessionStore";
import useTransactionStore from "../../../../stores/useTransactionStore";
import useAuthStore from "../../../../stores/useAuthStore";
import { generateDocumentNo } from "../../../../utils/documentSequenceUtils";
import { shouldRegenerateIdentityAtSave, captureOrderCreationSnapshot } from "../../../../utils/cartIdentityGuard";
import { assertDocumentNoUnique } from "../../../../utils/dataIntegrity";
import { resolveActingUserId } from "../../../../utils/actingUser";
import { logSequenceEvent } from "../../../../utils/sequenceAudit";
import { buildGiftCardReversalPayload } from "../../../../utils/giftCardReversal";

const useSuperMarketHandlers = ({
  cart,
  setCart,
  tillData,
  posConfig,
  pricePrecision,
  orderType,
  documentSequence,
  setDocumnetSequence,
  deleteCart,
  setGiftCardData,
  giftCardData,
  selectedProductInCart,
  setStockList,
  handleLoyaltyPayment,
  getFilteredPaymentMethods,
  setShowPayments,
  setSelectedDocumentType,
  searchInputRef,
  setShowInputFields,
  setCustomMixItems,
  setGroupQty,
  setActiveInput,
  setProductSearchInput,
  discardGiftCards,
  removeCutomer,
  upsertPOSLog,
  posConfigData,
  domainURL,
  selectedSaleTypeData,
  setIsNriBilling,
  setIsPanCustomer,
  hasShownCustomerModalRef,
  selectedCustomer,
  setSelectedCustomer,
  getCurrentMonthRange,
  setAmount,
  isExchange,
  syncOrders,
}) => {
  const history = useHistory();
  const { t } = useTranslation();
  const authHeaders = getOAuthHeaders();
  const deletePaymentInFlightRef = useRef(false);

  const getProductNearByStock = async () => {
    let setAuthTokens;
    if (authHeaders && authHeaders.access_token) {
      setAuthTokens = authHeaders.access_token;
    }
    try {
      const stockInputValues = {
        query: `
        query {
          getProductsNearbyStock(
            bUnitId: "${tillData.tillAccess.csBunit.csBunitId}",
            code: "${selectedProductInCart.value}"
          ) {
            csBunitId
            value
            name
            totalQty
            stores {
              csNearbyStoreId
              name
              nearbybUnitId
              viewStock
              isIssue
              warehouse {
                mWarehouseID
                name
                value
                storageBin {
                  mStoragebinID
                  name
                  stocks {
                    mStockID
                    qtyonhand
                    product {
                      mProductId
                      value
                      name
                      upc
                      uom {
                        csUomId
                        name
                      }
                      productCategory {
                        mProductCategoryId
                        value
                        name
                      }
                    }
                    batch {
                      mBatchId
                      batchno
                      upc
                      price
                      startdate
                      enddate
                      life
                      purchasePrice
                      listPrice
                      purchaseDate
                    }
                  }
                  totalQty
                }
              }
            }
          }
        }
      `,
      };

      const response = await getProductsNearbyStockService(stockInputValues);

      const { stores, totalQty, csBunitId } = response.data.data.getProductsNearbyStock;

      const currentStock = {
        csNearbyStoreId: "53F4FBEBDAB94C4AB205B7415EBD739E",
        name: tillData.tillAccess.csBunit.mWarehouse.name,
        nearbybUnitId: "EEF9FADCE0D74AE3B8B6096E6C35B6AA",
        viewStock: "Y",
        isIssue: "Y",
        warehouse: {
          mWarehouseID: "79FD0E516E4F4C5F9A7932F0C5C82C92",
          name: tillData.tillAccess.csBunit.mWarehouse.name,
          value: "Central WH TTLLC",
          storageBin: {
            mStoragebinID: "09581CA09C154830A20B88370C501FD6",
            name: "Central Warehouse",
            stocks: [],
            totalQty: totalQty || 0,
          },
        },
      };

      const isStorePresent = stores?.some((store) => store?.nearbybUnitId === csBunitId);
      setStockList(isStorePresent ? stores : [currentStock, ...stores]);
    } catch (error) {
      console.error("Error fetching nearby stock:", error);
    }
  };

  // Always reset return+exchange state on cancel — fixes issue where Cancel
  // button left exchangeCart and selectedReturnType=EXCHANGE in place, leaking
  // into the next bill.
  const clearReturnExchangeState = () => {
    useCartStore.getState().setExchangeCart(null);
    useTransactionStore.getState().setSelectedReturnType(null);
    useTransactionStore.getState().setReturnThisReceipt(null);
    useTransactionStore.getState().setRefundReturnCustomerId(null);
  };

  const cancelOrder = async () => {
    const hasAdvancePayment = Number(cart?.advancePayment || 0) > 0;
    const hasRecordedPayments = cart?.payments?.some((p) => Number(p.amount || 0) > 0);
    // BUG-1858: retrieved advance orders move their deposit into paymentData.
    const hasHistoricalPaymentData = cart?.paymentData?.some((p) => Number(p.amount || 0) > 0);
    if ((cart?.layAway === "Y" || cart?.type === "Layaway") && (hasAdvancePayment || hasRecordedPayments || hasHistoricalPaymentData)) {
      message.error("This order has existing payments and cannot be cancelled. Please process a return or refund instead.");
      return;
    }
    const isExchangeMode = useTransactionStore.getState().selectedReturnType === "EXCHANGE";
    const exchangeCartObj = isExchangeMode ? useCartStore.getState().exchangeCart : null;
    const currentCart = isExchangeMode && exchangeCartObj?.items?.length > 0 ? exchangeCartObj : useCartStore.getState().cart;
    if (posConfig?.saveParkedBills === "Y" && currentCart?.isRetrived === "Y") {
      const csBunitId = tillData?.tillAccess?.csBunit?.csBunitId;
      const cwrPosCartId = currentCart?.cwr_pos_cart_id || null;
      const cwrTillId = tillData?.tillAccess?.cwrTill?.cwrTillID;
      const status = "CL";

      const cartItems = currentCart;

      const cartPayload = {
        csBunitId,
        cwrPosCartId,
        cwrTillId,
        status,
        cartItems,
      };

      const response = await upsertCart(cartPayload);
      if (response?.status === 200) {
        if (response?.data?.data?.upsertCart?.status === "200") {
          // clearCart(cart.type);
          deleteCart();
          clearReturnExchangeState();
          setIsNriBilling(false);
          setIsPanCustomer(false);
          setSelectedDocumentType("Invoice");
          setShowInputFields(false);
          useTransactionStore.getState().setIsB2bEnabled(null);
          localStorage.removeItem("isB2bEnabled");
          localStorage.removeItem("customerAddressIds");
          localStorage.removeItem("isCreditSale");
          useTransactionStore.getState().setIsCreditSale(null);
          hasShownCustomerModalRef.current = false;
          if (searchInputRef?.current) {
            searchInputRef.current.focus();
          }
        }
      } else {
        message.warning("Something is wrong in upsertCart service");
      }
    } else if (posConfig?.saveCancelledOrders !== "Y") {
      deleteCart();
      clearReturnExchangeState();
      setIsNriBilling(false);
      setIsPanCustomer(false);
      setSelectedDocumentType("Invoice");
      setShowInputFields(false);
      useTransactionStore.getState().setIsB2bEnabled(null);
      localStorage.removeItem("isB2bEnabled");
      localStorage.removeItem("customerAddressIds");
      localStorage.removeItem("isCreditSale");
      useTransactionStore.getState().setIsCreditSale(null);
      hasShownCustomerModalRef.current = false;
      if (searchInputRef?.current) {
        searchInputRef.current.focus();
      }
    } else {
      let setAuthTokens;
      setSelectedDocumentType("Invoice");
      let PaymentMethods = getFilteredPaymentMethods() || [];
      let payments =
        PaymentMethods.length > 5
          ? sortBy(PaymentMethods, "sequenceNo").slice(
              0,
              tillData?.tillAccess?.csBunit?.b2cCustomer?.cwrCustomerId === cart?.customer?.cwrCustomerId && PaymentMethods.length === 5
                ? 5
                : cart.layAway === "Y" || cart?.creditSale === "Y"
                  ? 4
                  : 5
            )
          : PaymentMethods;

      setShowPayments(payments);
      if (authHeaders && authHeaders.access_token) {
        setAuthTokens = authHeaders.access_token;
      }
      if (cart?.couponInput?.length > 0) {
        const serverUrl = process.env.REACT_APP_serverUrl;

        const couponsData = [...cart.couponInput];
        // Function to discard a coupon
        const discardCoupon = async (coupon) => {
          const discardCouponQuery = {
            query: `query {
                  discardCoupon(couponcode: "${coupon.couponCode}", referenceId: "${coupon.referenceId}") {
                    status
                    message
                  }
                }`,
          };
          try {
            const response = await discardCouponService(coupon.couponCode, coupon.referenceId);

          } catch (error) {
            console.error(`Error discarding coupon ${coupon.couponCode}:`, error);
          }
        };
        couponsData.forEach((coupon) => {
          discardCoupon(coupon);
        });
      }

      discardGiftCards(cart);
      let index = cart.payments.findIndex((payment) => payment.name.toLowerCase() === "loyalty" && !cart.isReturn);
      if (index >= 0) {
        handleLoyaltyPayment(cart.payments[index], cart);
      }
      clearCart(cart.type);
      setSelectedDocumentType("Invoice");
      setIsNriBilling(false);
      setIsPanCustomer(false);
      useTransactionStore.getState().setIsB2bEnabled(null);
      localStorage.removeItem("isB2bEnabled");
      localStorage.removeItem("customerAddressIds");
      localStorage.removeItem("isCreditSale");
      hasShownCustomerModalRef.current = false;
      useTransactionStore.getState().setCouponAdditionalInfo(null);
      useCartStore.getState().setExchangeCart(null);
      useTransactionStore.getState().setIsCreditSale(null);
      useTransactionStore.getState().setReturnThisReceipt(null);
      setShowInputFields(false);
      setCustomMixItems([]);
      setGroupQty(1);
      setActiveInput("search");
      setProductSearchInput("");
      if (searchInputRef?.current) {
        searchInputRef.current.focus();
      }
    }
  };

  const handleDeletePayment = (payment) => {
    if (deletePaymentInFlightRef.current) return;
    deletePaymentInFlightRef.current = true;
    let activeCart = isExchange ? { ...(useCartStore.getState().exchangeCart || {}) } : { ...useCartStore.getState().cart };
    let paid = 0;
    Promise.all(
      activeCart.payments.map(async (pay) => {
        if (payment.name.toLowerCase() === "gift card" && payment?.finPaymentmethodId === pay?.finPaymentmethodId) {
          let setAuthTokens;
          if (authHeaders && authHeaders.access_token) {
            setAuthTokens = authHeaders.access_token;
          }
          // BUG-2078: unique referenceNo per active entry (not the shared activeCart.referenceId)
          // prevents this reversal from overwriting a prior removal's transaction row.
          const reversalPayload = buildGiftCardReversalPayload(
            giftCardData,
            tillData?.tillAccess?.cwrTill?.cwrTillID,
            activeCart.customer?.cwrCustomerId
          );

          if (reversalPayload) {
            const response = await upsertGiftCardTransaction({ query: reversalPayload.query });
            if (response.data.data.upsertGiftCardTransaction.status == 200) {
              // Fully reversed — clear instead of pushing stale negative rows that a
              // later redemption's findIndex() could pick up in place of the live entry.
              setGiftCardData([]);
            }
          }
        } else if (payment.name.toLowerCase() === "credit" && payment?.finPaymentmethodId === pay?.finPaymentmethodId) {
          const trxData = [
            {
              b2cCustomerId: activeCart?.customer?.cwrCustomerId,
              trxDate: dayjs(new Date()).format("YYYY-MM-DD"),
              trxAmt: Number(payment.amount),
              openingAmt: activeCart?.customer?.creditBalance - Number(payment.amount),
              closingAmt: activeCart?.customer?.creditBalance,
              type: "CR",
              cwrTillId: null,
              sOrderId: null,
              referenceNo: activeCart?.referenceId,
            },
          ];
          const creditUpsertResponse = await upsertCreditTransaction(trxData);
        } else if (payment.name.toLowerCase() === "loyalty" && payment?.finPaymentmethodId === pay?.finPaymentmethodId) {
          await handleLoyaltyPayment(payment, activeCart);
        } else if (payment.isWallet !== "Y" && payment.paymentEntryId && pay.paymentEntryId) {
          // Permanent fix (BUG-1829): every payment entry now carries a unique paymentEntryId
          // assigned at creation (paymentController.js addAmount). Deletion matches on that id
          // instead of the shared `name`, so multiple entries of the same method (Bank Transfer,
          // Card, etc.) no longer all get removed together. Falls back to the legacy name/authorization/
          // transactionId heuristics below only for entries created before this fix (missing the id).
          if (pay.paymentEntryId !== payment.paymentEntryId) {
            paid = paid + parseFloat(pay.amount);
            return pay;
          }
        } else if (pay.name !== payment.name && payment.name.toLowerCase() !== "card" && payment.isBankTransfer !== "Y") {
          paid = paid + parseFloat(pay.amount);
          return pay;
        } else if (payment.name.toLowerCase() === "card" && (payment.authorization ? pay.authorization !== payment.authorization : true)) {
          paid = paid + parseFloat(pay.amount);
          return pay;
        } else if (payment.isBankTransfer === "Y" && (payment.transactionId ? pay.transactionId !== payment.transactionId : pay.name !== payment.name)) {
          paid = paid + parseFloat(pay.amount);
          return pay;
        } else if (payment.isWallet === "Y" && payment.finPaymentmethodId === pay.finPaymentmethodId) {
          try {
            const tillData = useAuthStore.getState().tillData || {};
            const bunitId = tillData?.tillAccess?.csBunit?.csBunitId;

            const refundAmount = Number(payment.amount);

            const payload = {
              bunitId,
              customerId: activeCart.customer.cwrCustomerId,
              trxType: "DP", // Deposit back
              trxValue: refundAmount,
              tillId: tillData?.tillAccess.cwrTill.cwrTillID,
              payments: [],
              transactionDetails: "{}",
            };

            const response = await upsertWalletTransactionService(payload);

            if (response?.status === "200") {
              const updatedCustomer = {
                ...selectedCustomer[0],
                currentBalance: (selectedCustomer[0]?.currentBalance || 0) + refundAmount,
              };

              setSelectedCustomer([updatedCustomer]);
              message.success("Wallet amount refunded");
            } else {
              message.error("Wallet refund failed");
            }
          } catch (err) {
            console.error("Wallet refund error", err);
          }
        }
      })
    )
      .then((results) => {
        activeCart.payments = results.filter((result) => result !== undefined);
        activeCart.paid = paid + (activeCart?.advancePayment || 0);
        setAmount(parseFloat((activeCart.total - activeCart.paid).toFixed(pricePrecision)));
        if (payment?.name?.toLowerCase() === "cash") {
          activeCart.tenderedAmount = activeCart.tenderedAmount - payment.amount;
        } else if (payment?.name?.toLowerCase() === "gift card") {
          activeCart.giftCardNumbers = [];
        }
        if (isExchange) {
          useCartStore.getState().setExchangeCart(activeCart);
        } else {
          setCart(activeCart);
        }
      })
      .catch((error) => {
        // Handle errors
        console.error(error);
      })
      .finally(() => {
        deletePaymentInFlightRef.current = false;
      });
  };

  const clearCart = async (type) => {
    const tillSession = useSessionStore.getState().tillSession;
    const tillSessionId = tillSession.tillSessionId;
    // TIC-9233/BUG-2210: read the live cart, not the `cart` closure prop. This
    // function is captured once at render time; if the on-screen cart changes
    // (e.g. an advance order is saved) between that render and this call firing,
    // the stale closure's `cart` still points at the OLD order. Persisting a
    // zero-total cancelled snapshot built from it under that OLD order's
    // sOrderID silently overwrote a real, already-synced order in production
    // (confirmed live: a genuine advance order's db.orders row replaced with an
    // unrelated cancellation days later). Same stale-closure class already fixed
    // in processOrderApproved (useOrderProcessing.js) via the identical live read.
    const liveCart = useCartStore.getState().cart || cart || {};
    let cartToDb = JSON.parse(JSON.stringify(liveCart));
    cartToDb.orderTime = dayjs(new Date()).format("DD-MM-YYYY HH:mm:ss");
    cartToDb.createdBy = resolveActingUserId(tillData); // BUG-2232: acting cashier, not till-registration user
    cartToDb.orderType = orderType?.cwrSaletype?.cwrSaletypeId;
    // TSK-3249: order stays under the till's opening business date until store close.
    cartToDb.orderDate = tillSession.businessDate ? dayjs(tillSession.businessDate).format("DD-MM-YYYY") : dayjs(new Date()).format("DD-MM-YYYY");
    cartToDb.tillSessionId = tillSessionId;
    cartToDb.key = uuidv4().replace(/-/g, "").toUpperCase();
    cartToDb.isSynced = 0;
    cartToDb.syncAttempts = 0;
    cartToDb.customerSearchKey = liveCart?.customer?.code;
    cartToDb.tax = 0;
    cartToDb.total = 0;
    cartToDb.docType = "Invoice";
    cartToDb.totalQty = 0;
    cartToDb.couponInput = [];
    cartToDb.discount = 0;
    cartToDb.isReturn = false;
    cartToDb.paid = 0; // <-- FIX: ensure paid is 0 for cancelled order
    cartToDb.payments = []; // <-- FIX: ensure payments is empty for cancelled order
    cartToDb.change = 0; // <-- FIX: ensure change is 0
    const resetItemValues = (item) => {
      item.realWeight = item.weight;
      item.salePrice = 0;
      item.sunitprice = 0;
      item.netStd = 0;
      item.nettotal = 0;
      item.unitTax = 0;
      item.taxAmount = 0;
      item.weight = 0;
      item.unitPrice = 0;
      item.isReturn = false;
      item.netList = 0;
      item.discount = 0;
      item.linetax = 0;
      item.linenet = 0;
      item.linegross = 0;
      item.linestd = 0;
      item.netunit = 0;
      item.listPrice = 0;
      item.grossunit = 0;
      item.grossstd = 0;
      item.grosslist = 0;
      if (Array.isArray(item.children) && item.children.length > 0) {
        item.children.forEach((child) => resetItemValues(child));
      }
    };
    cartToDb.items.forEach((item) => resetItemValues(item));
    let freshCancelDocSeqType = null;
    let freshCancelDocPrefix = null;
    if (type === "Layaway") {
      const cancelledSeqLW = useSessionStore.getState().cancelledOrderSequence;
      if (cancelledSeqLW) {
        cartToDb.documentno = generateDocumentNo(cancelledSeqLW);
        useSessionStore.getState().incrementCancelledOrderSequence();
        freshCancelDocSeqType = "cancelled";
        freshCancelDocPrefix = cancelledSeqLW.prefix;
        deleteCart(true, "ownSequence");
      } else {
        deleteCart(true, liveCart.parked === "Y" ? "parked" : cartToDb.type);
      }
    } else {
      const cancelledSeq = useSessionStore.getState().cancelledOrderSequence;
      if (cancelledSeq) {
        cartToDb.documentno = generateDocumentNo(cancelledSeq);
        useSessionStore.getState().incrementCancelledOrderSequence();
        freshCancelDocSeqType = "cancelled";
        freshCancelDocPrefix = cancelledSeq.prefix;
        deleteCart(true, "ownSequence");
      } else {
        // F-19: don't preview the new cart's number (nextCartNo) yet. The DI-31
        // check below can still correct documentSequence out from under it, and
        // committing a preview to the on-screen cart (via deleteCart) BEFORE that
        // check runs is exactly how the cashier ends up seeing one bill number
        // while a different one actually saves. Defer deleteCart() until after
        // the check, once the counter this preview reads is final.
        freshCancelDocSeqType = "regular";
        freshCancelDocPrefix = tillData.tillAccess.cwrTill.prefix;
      }
    }
    // DI-31 (cancel-path): the documentno just generated above comes from a purely
    // local counter (cancelledOrderSequence / documentSequence) that can drift out
    // of sync with numbers other tills/sessions already assigned to real orders —
    // the same class of gap that produced duplicate invoice numbers on the regular
    // completion path (see useOrderProcessing.js DI-31, RAMS/21320 and related
    // production incidents). Re-validate here before persisting; non-blocking,
    // self-heals via assertDocumentNoUnique. Skipped when no fresh number was
    // generated this call (documentno left as the existing advance/PB number).
    if (freshCancelDocSeqType && freshCancelDocPrefix) {
      try {
        const cancelDocDupCheck = await assertDocumentNoUnique({
          dbAdapter: db,
          documentno: cartToDb.documentno,
          sOrderID: cartToDb.sOrderID,
          prefix: freshCancelDocPrefix,
          sessionStore: useSessionStore,
          sequenceType: freshCancelDocSeqType,
        });
        if (cancelDocDupCheck.recovered) {
          cartToDb.documentno = cancelDocDupCheck.documentno;
        }
      } catch (cancelDocErr) {
        console.warn("[DI-31] Cancel-path documentno uniqueness check failed (non-blocking):", cancelDocErr?.message);
      }
    }
    // F-19: only now — after any DI-31 recovery above has landed in the session
    // store — derive and commit the new cart's preview number, so it can never be
    // stale relative to a correction this very check just applied. Reads the live
    // store value rather than the `documentSequence` prop, which a same-tick
    // recovery wouldn't update (React state, not read synchronously here).
    if (freshCancelDocSeqType === "regular") {
      const liveDocumentSequence = useSessionStore.getState().documentSequence;
      const newDocumentSequence = liveCart.parked === "Y" ? liveDocumentSequence : liveDocumentSequence + 1;
      const nextCartNo = `${freshCancelDocPrefix}${newDocumentSequence + 1}`;
      useSessionStore.getState().setDocumentSequence(newDocumentSequence);
      setDocumnetSequence(newDocumentSequence);
      deleteCart(true, liveCart.parked === "Y" ? "parked" : cartToDb.type, false, nextCartNo);
    }
    // DI-33 (cancel-path): this write was never covered by the stale-identity guard
    // added to processOrderApproved — a cart resurrected here (retrieved/reloaded)
    // can still carry the sOrderID of an ALREADY-COMPLETED sale. db.orders is keyed
    // on sOrderID, so writing the zero-total cancelled snapshot under that id would
    // silently overwrite the real sale (confirmed in production: VRP/101112, a real
    // ₹326.40 sale, was replaced by CAN/319 this way). Regenerate sOrderID only when
    // it would clobber a different existing order.
    try {
      const existingOrderForCancel = cartToDb.sOrderID ? await db.orders.get(cartToDb.sOrderID) : null;
      const preorderPrefixCancel = useSessionStore.getState().preorderSequence?.prefix;
      if (
        shouldRegenerateIdentityAtSave({
          existingOrder: existingOrderForCancel,
          cart: cartToDb,
          documentno: cartToDb.documentno,
          preorderPrefix: preorderPrefixCancel,
        })
      ) {
        const staleSOrderID = cartToDb.sOrderID;
        cartToDb.sOrderID = uuidv4().replace(/-/g, "").toUpperCase();
        console.warn("[DI-33] Stale sOrderID detected at cancel — regenerated to prevent order overwrite.", {
          staleSOrderID,
          newSOrderID: cartToDb.sOrderID,
          documentno: cartToDb.documentno,
          overwrittenDocumentno: existingOrderForCancel.documentno,
        });
        logSequenceEvent(
          "stale-identity-recovery",
          {
            documentno: cartToDb.documentno,
            staleSOrderID,
            newSOrderID: cartToDb.sOrderID,
            existingDocumentno: existingOrderForCancel.documentno,
            flow: "clearCart",
          },
          db
        );
        // BUG-2175: a stale sOrderID can carry a stale documentno with it — regenerating
        // only the sOrderID above stops THIS write from overwriting existingOrderForCancel's
        // row, but if we stop there this cancel record still saves under that row's OLD
        // documentno: two intact orders sharing one receipt number (confirmed live: OFC
        // SSZR04/104727, a completed 32.89 sale and this exact kind of stale-identity cancel
        // record both landed under the same documentno, the second one surfacing as a
        // phantom "Layaway" status on that receipt number). Same fix already applied to
        // processOrderApproved (useOrderProcessing.js) for the identical gap — re-run the
        // same soft, non-blocking uniqueness check now that sOrderID is fresh so it can
        // actually see the collision.
        try {
          const cancelIdentityDocDupCheck = await assertDocumentNoUnique({
            dbAdapter: db,
            documentno: cartToDb.documentno,
            sOrderID: cartToDb.sOrderID,
            prefix: freshCancelDocPrefix || tillData.tillAccess.cwrTill.prefix,
            sessionStore: useSessionStore,
            sequenceType: freshCancelDocSeqType || "regular",
          });
          if (cancelIdentityDocDupCheck.recovered) {
            cartToDb.documentno = cancelIdentityDocDupCheck.documentno;
          }
        } catch (cancelIdentityDocErr) {
          console.warn("[DI-33] Post-regeneration documentno uniqueness check failed at cancel (non-blocking):", cancelIdentityDocErr?.message);
        }
      }
    } catch (diErr) {
      console.warn("[DI-33] Stale identity check failed at cancel (non-blocking):", diErr?.message);
    }
    // Persist and sync the cancelled-order snapshot regardless of type — this ran
    // unconditionally before the cancelled-sequence feature was added; a later
    // refactor accidentally nested it inside the non-Layaway branch only, which
    // silently dropped the cancel record (and its server sync) for Layaway/Advance
    // cancellations.
    // Issue-7 / FK-3692,6319: see captureOrderCreationSnapshot's own docblock.
    captureOrderCreationSnapshot(cartToDb);
    await db.orders.put(cartToDb);
    if (posConfig?.disableOfflineBilling === "Y") {
      syncOrders("auto");
    }
    useTransactionStore.getState().setSelectedReturnType(null);
    setGiftCardData([]);
  };

  const handleLogout = async (setLogoutLoading) => {
    setLogoutLoading(true);
    let parkedBills = useSessionStore.getState().parkedList;
    let posConfig = usePOSConfigStore.getState().posConfig;
    if (posConfig?.saveParkedBills === "Y") {
      try {
        const dateRange = getCurrentMonthRange();

        const filterBy = JSON.stringify({
          DateRange: dateRange,
        });

        const sortBy = JSON.stringify({
          created: "DESC",
        });

        const response = await cartHistory({
          q: "",
          filter_by: filterBy,
          sort_by: sortBy,
          tillId: tillData?.tillAccess?.cwrTill?.cwrTillID || null,
        });

        if (response?.status === 200) {
          const data = JSON.parse(response?.data?.data?.cartHistory || "[]");

          const parkedBillsFromAPI = data.filter((item) => item?.cartstatus === "DR");

          if (parkedBillsFromAPI.length > 0) {
            message.error(`${t("unlink_till_parked_bill_error")}`);
            setLogoutLoading(false);
            return;
          }
        }
      } catch (error) {
        console.error("Error checking parked bills:", error);
        setLogoutLoading(false);
        return;
      }
    } else {
      if (parkedBills?.length > 0) {
        message.error(`${t("unlink_till_parked_bill_error")}`);
        setLogoutLoading(false);
        return;
      }
    }

    if (cart?.items?.length > 0) {
      {
        message.error(`${t("unlink_till_cart_error")}`);
      }
      setLogoutLoading(false);
    } else {
      if (posConfigData?.posType === "PRD") {
        const cookiesToDelete = document.cookie.split(";").map((cookie) => cookie.trim());
        for (let i = 0; i < cookiesToDelete.length; i++) {
          const cookie = cookiesToDelete[i];
          const eqPos = cookie.indexOf("=");
          const name = eqPos > -1 ? cookie.substr(0, eqPos) : cookie;
          const domain = "domain=" + domainURL;
          document.cookie = name + "=;expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; secure=true; SameSite=Strict;" + domain;
        }
        useSessionStore.getState().setTillSession(null);
        window.location.assign("/");
        setLogoutLoading(false);
      } else {
        removeCutomer();
        openDrawer();
        upsertPOSLog(cart, "LGO");
        useSessionStore.getState().setLocked(false);
        if (posConfig?.shiftClose === "Y") {
          // Close-till flow: count cash → reconcile → close → sign-in
          history.push("/close-till");
        } else {
          // No shift close: clear auth, go to sign-in for next cashier
          const cookiesToDelete = document.cookie.split(";").map((c) => c.trim());
          for (const c of cookiesToDelete) {
            const name = c.split("=")[0];
            document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=${domainURL}`;
          }
          useSessionStore.getState().setTillSession(null);
          useAuthStore.getState().logout();
          // BUG-2026: await the shell's logout IPC before navigating — it's the
          // authoritative teardown; firing-and-forgetting it raced the redirect.
          await window.electronAPI?.auth?.logout();
          window.location.assign("/sign-in");
        }
        setLogoutLoading(false);
      }
    }
  };

  return {
    getProductNearByStock,
    cancelOrder,
    handleDeletePayment,
    clearCart,
    handleLogout,
  };
};

export default useSuperMarketHandlers;
